// frontend/src/components/TeamMemberView.js
import React, { useState, useEffect } from 'react';

// Import block display components
import TextBlockDisplay from './ProfileBlockDisplays/TextBlockDisplay';
import WordCloudDisplay from './ProfileBlockDisplays/WordCloudDisplay';
import StatsBlockDisplay from './ProfileBlockDisplays/StatsBlockDisplay';
import BarsBlockDisplay from './ProfileBlockDisplays/BarsBlockDisplay';
import ImageBlockDisplay from './ProfileBlockDisplays/ImageBlockDisplay';
import ModernCarousel from './ModernCarousel';
import { ensureSecureUrl } from '../utils/urlHelper';
import { EditButton } from './InlineFieldEditor';

const TeamMemberView = ({ 
    member, 
    isEditor = false, 
    visibilitySettings = {}, 
    onToggleVisibility = () => { },
    onEdit = () => { }
}) => {
    const [maxPhotoWidth, setMaxPhotoWidth] = useState(null);
    const [photoHeight, setPhotoHeight] = useState(400);

    // Calculate the optimal photo dimensions after member data is loaded
    useEffect(() => {
        if (!member?.photos || member.photos.length <= 1) return;

        const CONTAINER_PADDING = 32;
        const BOX_PADDING = 64;
        const CAROUSEL_PADDING = 96;
        const SAFETY_MARGIN = 32;

        const TOTAL_PADDING = CONTAINER_PADDING + BOX_PADDING + CAROUSEL_PADDING + SAFETY_MARGIN;
        const MAX_SCREEN_WIDTH = window.innerWidth - TOTAL_PADDING;

        const imagePromises = (member.photos || []).map((photo) => {
            return new Promise((resolve) => {
                const img = new Image();
                img.onload = () => {
                    const aspectRatio = img.naturalWidth / img.naturalHeight;
                    resolve({ aspectRatio, width: img.naturalWidth, height: img.naturalHeight });
                };
                img.onerror = () => resolve({ aspectRatio: 0, width: 0, height: 0 });
                img.src = ensureSecureUrl(photo.url);
            });
        });

        Promise.all(imagePromises).then((images) => {
            const widestImage = images.reduce((max, img) =>
                img.aspectRatio > max.aspectRatio ? img : max
                , { aspectRatio: 0 });

            if (widestImage.aspectRatio === 0) return;

            const optimalHeight = MAX_SCREEN_WIDTH / widestImage.aspectRatio;
            const constrainedHeight = Math.min(optimalHeight, 600);
            const actualWidth = constrainedHeight * widestImage.aspectRatio;

            setPhotoHeight(constrainedHeight);
            setMaxPhotoWidth(actualWidth);
        });
    }, [member]);

    const renderProfileBlock = (block, index) => {
        const blockProps = { content: block.content };

        switch (block.type) {
            case 'text':
                return <TextBlockDisplay key={index} {...blockProps} />;
            case 'wordcloud':
                return <WordCloudDisplay key={index} {...blockProps} />;
            case 'stats':
                return <StatsBlockDisplay key={index} {...blockProps} />;
            case 'bars':
                return <BarsBlockDisplay key={index} {...blockProps} />;
            case 'image':
                return <ImageBlockDisplay key={index} {...blockProps} />;
            default:
                return null;
        }
    };

    if (!member) return null;

    const profilePhoto = member?.profilePhoto || (member?.photos && member.photos[0]);

    // Helper for editor toggles
    const VisibilityToggle = ({ field, label }) => {
        if (!isEditor) return null;
        const isVisible = visibilitySettings[field] !== false; // Default true if undefined

        return (
            <label 
                className="relative inline-flex items-center cursor-pointer group/toggle z-20" 
                title={isVisible ? `${label} ausblenden` : `${label} einblenden`}
                onClick={(e) => e.stopPropagation()}
            >
                <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={isVisible}
                    onChange={() => onToggleVisibility(field, !isVisible)}
                />
                <div className="w-10 h-5 bg-surface-containerHighest/80 backdrop-blur-sm border border-outlineVariant/50 rounded-full peer peer-checked:after:translate-x-[20px] peer-checked:after:border-white after:content-[''] after:absolute after:top-[2.5px] after:start-[2.5px] after:bg-surface-onVariant/40 after:rounded-full after:h-[15px] after:w-[15px] after:transition-all peer-checked:bg-primary peer-checked:after:bg-primary-on peer-checked:after:opacity-100 group-hover/toggle:border-primary/50 shadow-sm" />
            </label>
        );
    };

    const sectionClass = (field, isDefaultVisible = true) => {
        if (!isEditor) return '';
        const isVisible = visibilitySettings[field] !== undefined ? visibilitySettings[field] : isDefaultVisible;
        return isVisible ? 'relative group/section' : 'relative group/section opacity-40 grayscale-[0.5] hover:opacity-100 hover:grayscale-0 transition-all duration-500';
    };

    return (
        <div className="w-full max-w-screen-xl mx-auto px-4 py-8 relative">
            {isEditor && (
                <div className="mb-8 p-4 bg-primary/5 border border-primary/20 rounded-2xl flex items-center justify-between text-primary">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary text-primary-on flex items-center justify-center font-bold">!</div>
                        <p className="font-bold text-sm">VORSCHAU-EDITOR: Klicke auf die Schalter, um Bereiche zu aktivieren/deaktivieren.</p>
                    </div>
                </div>
            )}

            {/* Header with Profile Photo - Standard Teil */}
            <div className={`p-6 md:p-10 mb-8 rounded-3xl border border-outlineVariant bg-surface flex flex-col md:flex-row gap-8 items-center md:items-start ${sectionClass('header')}`}>
                {/* Profile Photo / Avatar */}
                {(() => {
                    const avatarUrl = member?.avatarUrl || member?.avatarConfig?.modelUrl;
                    const imageUrl = ensureSecureUrl(profilePhoto?.url || avatarUrl);
                    const isAvatarOnly = !profilePhoto?.url && avatarUrl;

                    if (!imageUrl) return null;

                    return (
                        <div className="flex-shrink-0">
                            <div className="w-64 h-64 rounded-xl overflow-hidden shadow-elevation-2 bg-background-paper mx-auto md:mx-0">
                                <img
                                    src={imageUrl}
                                    alt={member.name}
                                    className={`w-full h-full ${isAvatarOnly ? 'object-contain' : 'object-cover'}`}
                                />
                            </div>
                        </div>
                    );
                })()}

                {/* Name, Age, Group, Description */}
                <div className="flex-grow text-center md:text-left space-y-4">
                    <h1 className="text-display-small font-bold text-surface-on leading-tight flex items-center justify-center md:justify-start">
                        {member.name}
                        {isEditor && <EditButton onClick={() => onEdit('scoutName', member.userData?.scoutName || member.name)} />}
                    </h1>

                    <div className={`${sectionClass('showAge')}`}>
                        <div className="absolute top-0 -right-2"><VisibilityToggle field="showAge" label="Alter" /></div>
                        {member.age && (
                            <p className="text-headline-small text-surface-onVariant flex items-center justify-center md:justify-start">
                                {member.age} Jahre alt
                                {isEditor && <EditButton onClick={() => onEdit('birthDate', member.userData?.birthDate)} />}
                            </p>
                        )}
                        {!member.age && isEditor && (
                            <p className="text-body-small italic text-surface-onVariant/50 flex items-center justify-center md:justify-start">
                                (Alter nicht gesetzt)
                                <EditButton onClick={() => onEdit('birthDate', member.userData?.birthDate)} />
                            </p>
                        )}
                    </div>

                    <div className={`${sectionClass('showJob')}`}>
                        <div className="absolute top-0 -right-2"><VisibilityToggle field="showJob" label="Aufgabe" /></div>
                        {member.job && (
                            <p className="text-headline-small font-bold text-primary flex items-center justify-center md:justify-start">
                                {member.job}
                                {isEditor && <EditButton onClick={() => onEdit('job', member.job)} />}
                            </p>
                        )}
                        {!member.job && isEditor && (
                            <p className="text-body-small italic text-surface-onVariant/50 flex items-center justify-center md:justify-start">
                                (Aufgabe nicht gesetzt)
                                <EditButton onClick={() => onEdit('job', member.job)} />
                            </p>
                        )}
                    </div>

                    <div className={`${sectionClass('showShortDescription')}`}>
                        <div className="absolute top-0 -right-2"><VisibilityToggle field="showShortDescription" label="Kurzbeschreibung" /></div>
                        {member.shortDescription && (
                            <p className="text-title-medium font-medium text-surface-on italic opacity-80 flex items-center justify-center md:justify-start">
                                "{member.shortDescription}"
                                {isEditor && <EditButton onClick={() => onEdit('shortDescription', member.shortDescription)} />}
                            </p>
                        )}
                        {!member.shortDescription && isEditor && (
                            <p className="text-body-small italic text-surface-onVariant/50 flex items-center justify-center md:justify-start">
                                (Keine Kurzbeschreibung)
                                <EditButton onClick={() => onEdit('shortDescription', member.shortDescription)} />
                            </p>
                        )}
                    </div>

                    <div className={`${sectionClass('showDescription')}`}>
                        <div className="absolute top-0 -right-2"><VisibilityToggle field="showDescription" label="Beschreibung" /></div>
                        {member.description && (
                            <div className="text-body-large text-surface-on leading-relaxed text-lg pt-2 border-t border-outlineVariant/20 whitespace-pre-wrap relative group/desc">
                                {member.description}
                                {isEditor && <EditButton onClick={() => onEdit('description', member.description)} className="absolute top-2 right-0" />}
                            </div>
                        )}
                        {!member.description && isEditor && (
                            <p className="text-body-small italic text-surface-onVariant/50 border-t border-outlineVariant/20 pt-2 flex items-center justify-center md:justify-start">
                                (Keine Beschreibung)
                                <EditButton onClick={() => onEdit('description', member.description)} />
                            </p>
                        )}
                    </div>
                </div>

                {/* Contact Info (Email, Phone) */}
                {(member.officialEmail || member.email || member.phone || isEditor) && (
                    <div className={`flex-shrink-0 md:w-64 bg-surface-containerLow p-6 rounded-2xl border border-outlineVariant self-stretch md:self-auto flex flex-col gap-6`}>
                        <h3 className="text-title-medium font-bold text-surface-on pb-2 border-b border-outlineVariant/20">Kontakt</h3>

                        <div className={`${sectionClass('showEmail', false)}`}>
                            <div className="absolute top-0 -right-2"><VisibilityToggle field="showEmail" label="E-Mail" /></div>
                            <h4 className="text-label-small font-bold text-surface-onVariant uppercase tracking-wider mb-1">E-Mail</h4>
                            {member.officialEmail || member.email ? (
                                <a href={`mailto:${member.officialEmail || member.email}`} className={`text-primary hover:underline break-all ${member.officialEmail ? 'font-bold' : 'font-medium'}`}>
                                    {member.officialEmail || member.email}
                                </a>
                            ) : (
                                <p className="text-label-small italic opacity-40">Keine E-Mail</p>
                            )}
                        </div>

                        <div className={`${sectionClass('showPhone', false)}`}>
                            <div className="absolute top-0 -right-2"><VisibilityToggle field="showPhone" label="Telefon" /></div>
                            <h4 className="text-label-small font-bold text-surface-onVariant uppercase tracking-wider mb-1">Telefon</h4>
                            {member.phone ? (
                                <a href={`tel:${member.phone}`} className="text-primary hover:underline break-all font-medium">
                                    {member.phone}
                                </a>
                            ) : (
                                <p className="text-label-small italic opacity-40">Keine Nummer</p>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Photo Gallery */}
            {( (member.photos && member.photos.length > 1) || isEditor) && (
                <div className={`${sectionClass('showPhotos')} p-6 md:p-8 mb-8 rounded-3xl border border-outlineVariant bg-surface`}>
                    <div className="absolute top-4 right-4"><VisibilityToggle field="showPhotos" label="Fotogalerie" /></div>
                    <h2 className="text-headline-medium font-bold text-surface-on mb-6 flex items-center gap-4">
                        Fotos
                        {isEditor && <EditButton onClick={() => onEdit('gallery', member.photos)} />}
                        {!member.photos?.length && isEditor && <span className="text-label-small font-medium text-surface-onVariant/40">(Keine Fotos vorhanden)</span>}
                    </h2>
                    {member.photos && member.photos.length > 1 ? (
                        <ModernCarousel variableWidth={true}>
                            {member.photos.map((photo, index) => (
                                <div
                                    key={photo._id || index}
                                    className="rounded-xl overflow-hidden flex-shrink-0 mx-2 shadow-elevation-1"
                                    style={{
                                        width: 'auto',
                                        maxWidth: maxPhotoWidth ? `${maxPhotoWidth}px` : 'calc(100vw - 6rem)',
                                        height: `${photoHeight}px`
                                    }}
                                >
                                    <img
                                        src={ensureSecureUrl(photo.url)}
                                        alt={`${member.name} ${index + 1}`}
                                        className="w-full h-full object-contain"
                                    />
                                </div>
                            ))}
                        </ModernCarousel>
                    ) : isEditor && (
                        <div className="h-40 border-2 border-dashed border-outlineVariant/30 rounded-2xl flex items-center justify-center text-surface-onVariant/30 italic">
                            Galerie benötigt mindestens 2 Bilder
                        </div>
                    )}
                </div>
            )}


            {/* Profile Blocks */}
            {( (member.profileBlocks && member.profileBlocks.length > 0) || isEditor) && (
                <div className={`${sectionClass('showBlocks')} flex flex-col gap-8`}>
                    <div className="relative border-t border-primary/10 pt-8 mt-4">
                        <div className="absolute top-4 right-4"><VisibilityToggle field="showBlocks" label="Individuelle Blöcke" /></div>
                        {member.profileBlocks && member.profileBlocks.length > 0 ? (
                            <div className="flex flex-col gap-8">
                                {member.profileBlocks
                                    .sort((a, b) => a.order - b.order)
                                    .map((block, index) => renderProfileBlock(block, index))}
                            </div>
                        ) : isEditor && (
                            <div className="p-12 border-2 border-dashed border-outlineVariant/30 rounded-[3rem] text-center text-surface-onVariant/20 italic">
                                Keine individuellen Blöcke vorhanden
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TeamMemberView;
